import Clickable from "@components/clickable";
import { getDrillDownNavParams } from "@components/drillDown/DrillDown.utils";
import { IGetValueArgs, not } from "@components/smart/FieldInfo";
import {
    AttachmentsAdditionalProperties,
    AttachmentsDef,
    getAttachmentsColumnDef,
    getAttachmentsFilterDef,
    getBasePairedDocumentFormFieldDef,
    NoteFieldDef,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import SmartFastEntryList, { ActionButtonPosition } from "@components/smart/smartFastEntryList";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IFormGroupDef, IGroupRenderFn } from "@components/smart/smartFormGroup/SmartFormGroup";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import {
    BusinessPartnerEntity,
    DocumentEntity,
    DocumentItemEntity,
    EntitySetName,
    EntityTypeName,
    GrantBalanceEntity,
    GrantClaimEntity,
    GrantEntity,
    GrantEntryEntity,
    IGrantStatusEntity
} from "@odata/GeneratedEntityTypes";
import { CurrencyCode, GrantStatusCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { PAYMENT_GROUP } from "@pages/banks/bankTransactions/BankTransactions.utils";
import { DocumentBusinessPartnerAdditionalProperties } from "@pages/businessPartner/BusinessPartner.shared.utils";
import { getBusinessPartnerFieldDef } from "@pages/businessPartner/BusinessPartner.utils";
import { ATTACHMENTS_COLUMN_LOCAL_CONTEXT } from "@pages/documents/Document.utils";
import { addBusinessPartnerTableDefs } from "@pages/documents/DocumentCommonDefs";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { AllTabsMain, AllTabsTableHeader, AllTabsWrapper, GrantActionButton } from "@pages/grant/Grant.styles";
import { getDocsUsedByGrantDef, getDraftDocsUsedByGrantDef } from "@pages/grant/Grant.utils";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "@pages/numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "@pages/PageUtils";
import { companyCurrencyFormatterWithoutUnit, getCompanyCurrencyUnit } from "@utils/CompanyUtils";
import { isNewEntity } from "@utils/definition.utils";
import i18next from "i18next";
import React from "react";
import { ROUTE_GRANT_JOURNAL } from "routes";

import { DASH_CHARACTER } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, Sort, ValidatorType } from "../../enums";
import { ColoredText } from "../../global.style";
import { TValue } from "../../global.types";
import { Model } from "../../model/Model";
import CurrencyType, { formatCurrency } from "../../types/Currency";
import { IFormDef } from "../../views/formView/Form";
import { renderFormTable } from "../../views/formView/FormRender.utils";
import { FormStorage } from "../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";
import GrantFormView from "./GrantFormView";
import TestIds from "../../testIds";


const statusFormatter = (val: TValue, values: IEntity) => {
    const colorWrapper = <ColoredText
        color={getStatusColor(values.entity?.Balance?.Status?.Code)}> {val} </ColoredText>;
    return { tooltip: val as string, value: colorWrapper };
};

const getStatusColor = (statusCode: GrantStatusCode) => {
    switch (statusCode) {
        case GrantStatusCode.InProgress:
            return "C_SEM_text_good";
        case GrantStatusCode.Applied:
            return "C_SEM_text_warning";
        case GrantStatusCode.Closed:
            return "C_SEM_text_bad";
    }
};

const _currencyFormatter = (val: TValue) => {
    return CurrencyType.format(val, { currency: CurrencyCode.CzechKoruna });
};

const _isSummaryVisible = (args: IGetValueArgs) => {
    return !args.storage.data.bindingContext.isNew();
};

const currencyFormatterField = {
    formatter: (val: TValue, args: IFormatOptions) => {
        return formatCurrency(val, CurrencyCode.CzechKoruna);
    }
};

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const summary: ISummaryItem[] = [{
        id: createPath(GrantEntity.Balance, GrantBalanceEntity.Status),
        label: i18next.t("Grants:Form.State"),
        colorFormatter: (val: TValue) => {
            const status = val as IGrantStatusEntity;
            return getStatusColor(status?.Code as GrantStatusCode);
        },
        formatter: (val: TValue, args: IFormatOptions) => {
            return (val as IGrantStatusEntity)?.Name;
        }
    }, {
        id: createPath(GrantEntity.Balance, GrantBalanceEntity.Amount),
        label: i18next.t("Grants:Form.AmountSummary"),
        formatter: _currencyFormatter,
        isVisible: _isSummaryVisible
    }, {
        id: createPath(GrantEntity.Balance, GrantBalanceEntity.UsedAmount),
        label: i18next.t("Grants:Form.UsedAmount"),
        formatter: _currencyFormatter,
        isVisible: _isSummaryVisible
    }, {
        id: createPath(GrantEntity.Balance, GrantBalanceEntity.RefundAmount),
        label: i18next.t("Grants:Form.RefundAmount"),
        formatter: _currencyFormatter,
        isVisible: _isSummaryVisible
    }, {
        id: createPath(GrantEntity.Balance, GrantBalanceEntity.BalanceAmount),
        label: i18next.t("Grants:Form.BalanceAmount"),
        formatter: _currencyFormatter,
        isVisible: _isSummaryVisible
    }];

    const filterBarDef: IFilterGroupDef [] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        defaultFilters: [GrantEntity.NumberOurs, createPath(GrantEntity.Balance, GrantBalanceEntity.Status), GrantEntity.BusinessPartner, GrantEntity.ShortName, GrantEntity.Note,
            GrantEntity.Name, GrantEntity.ContractNumber, GrantEntity.DateStart, GrantEntity.DateEnd, GrantEntity.DateDeadlineForSettling],
        isValueHelp: true,
        filterDefinition: {
            [GrantEntity.NumberOurs]: {},
            [GrantEntity.Name]: {},
            [GrantEntity.Note]: {},
            [GrantEntity.ShortName]: {},
            [ATTACHMENTS_COLUMN_LOCAL_CONTEXT]: getAttachmentsFilterDef(),
            ...withDisplayName(createPath(GrantEntity.Balance, GrantBalanceEntity.Status)),
            [GrantEntity.RequiredParticipation]: {},
            [GrantEntity.ContractNumber]: {},
            [GrantEntity.DateStart]: {},
            [GrantEntity.DateEnd]: {},
            [GrantEntity.DateDeadlineForSettling]: {},
            [createPath(GrantEntity.Balance, GrantBalanceEntity.Amount)]: currencyFormatterField,
            [createPath(GrantEntity.Balance, GrantBalanceEntity.UsedAmount)]: currencyFormatterField,
            [createPath(GrantEntity.Balance, GrantBalanceEntity.RefundAmount)]: currencyFormatterField,
            [createPath(GrantEntity.Balance, GrantBalanceEntity.BalanceAmount)]: currencyFormatterField
        }
    }];

    const table: ISplitPageTableDef = {
        id: "GrantTable",
        columns: [
            GrantEntity.NumberOurs,
            createPath(GrantEntity.Balance, GrantBalanceEntity.Status),
            GrantEntity.ShortName,
            GrantEntity.BusinessPartner,
            GrantEntity.DateDeadlineForSettling,
            createPath(GrantEntity.Balance, GrantBalanceEntity.Amount),
            createPath(GrantEntity.Balance, GrantBalanceEntity.UsedAmount),
            createPath(GrantEntity.Balance, GrantBalanceEntity.RefundAmount),
            createPath(GrantEntity.Balance, GrantBalanceEntity.BalanceAmount)
        ],
        title: i18next.t("Grants:Title"),
        tabs: [{
            id: GrantStatusCode.Applied,
            title: i18next.t("Grants:Tabs.Open"),
            filterNames: [GrantStatusCode.Applied, GrantStatusCode.InProgress]
        },
            { id: GrantStatusCode.Closed, title: i18next.t("Grants:Tabs.Closed") },
            { id: null, title: i18next.t("Grants:Tabs.All") }
        ],
        tabsSettings: {
            filterFieldName: createPath(GrantEntity.Balance, GrantBalanceEntity.StatusCode)
        },
        initialSortBy: [{ id: GrantEntity.NumberOurs, sort: Sort.Desc }],
        filterBarDef,
        columnDefinition: {
            [createPath(GrantEntity.Balance, GrantBalanceEntity.Amount)]: currencyFormatterField,
            [GrantEntity.NumberOurs]: {},
            [GrantEntity.Name]: {},
            [GrantEntity.Note]: {},
            [GrantEntity.ShortName]: {},
            ...withDisplayName(createPath(GrantEntity.Balance, GrantBalanceEntity.Status), {
                formatter: statusFormatter
            }),
            ...withDisplayName(GrantEntity.BusinessPartner, {}),
            [GrantEntity.ContractNumber]: {},
            [GrantEntity.DateStart]: {},
            [GrantEntity.RequiredParticipation]: {},
            [GrantEntity.DateEnd]: {},
            [GrantEntity.DateDeadlineForSettling]: {},
            [DocumentEntity.Attachments]: getAttachmentsColumnDef(EntityTypeName.Grant),
            [createPath(GrantEntity.Balance, GrantBalanceEntity.UsedAmount)]: currencyFormatterField,
            [createPath(GrantEntity.Balance, GrantBalanceEntity.RefundAmount)]: currencyFormatterField,
            [createPath(GrantEntity.Balance, GrantBalanceEntity.BalanceAmount)]: currencyFormatterField
        }
    };

    const pairCols = [
        { id: GrantClaimEntity.GrantClaimTypeCode },
        { id: createPath(GrantClaimEntity.Document, DocumentEntity.DateAccountingTransaction) },
        { id: createPath(GrantClaimEntity.Document) },
        { id: createPath(GrantClaimEntity.DocumentItem, DocumentItemEntity.Description) },
        { id: GrantClaimEntity.Amount }
    ];

    const form: IFormDef = {
        id: "GrantForm",
        translationFiles: getDefinitions.translationFiles,
        formControl: GrantFormView,
        files: AttachmentsDef,
        isDeletable: true,
        summary: [getNumberOursSummaryDef(EntitySetName.Grants), ...summary],
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Grants:Form:NewFormTitle"), storage.data.entity?.NumberOurs),
        title: i18next.t("Grants:Title"),
        additionalProperties: [DocumentBusinessPartnerAdditionalProperties, ...NumberRangeAdditionalProperties,
            { id: createPath(GrantEntity.Balance, GrantBalanceEntity.StatusCode) },
            { id: createPath(GrantEntity.GrantClaims, GrantClaimEntity.Document, DocumentEntity.DocumentType) },
            ...AttachmentsAdditionalProperties,
        ],
        groups: [{
            id: "Note",
            title: i18next.t("Common:General.Note"),
            rows: [[{ id: GrantEntity.Note }]]
        }, {
            id: "Grant",
            title: i18next.t("Grants:Title"),
            rows: [[{ id: GrantEntity.ShortName }, { id: GrantEntity.Name }], [
                { id: GrantEntity.ContractNumber }],
                [{ id: GrantEntity.DateStart }, { id: GrantEntity.DateEnd }, { id: GrantEntity.DateDeadlineForSettling }]]
        }, {
            id: "BusinessPartner",
            title: i18next.t("Grants:Provider"),
            rows: [[{ id: createPath(GrantEntity.BusinessPartner, BusinessPartnerEntity.Name) }, { id: createPath(GrantEntity.BusinessPartner, BusinessPartnerEntity.LegalNumber) },
                { id: createPath(GrantEntity.BusinessPartner, BusinessPartnerEntity.TaxNumber) }]]
        }, {
            id: "Pair",
            title: i18next.t("Grants:EntitlementsAndRefunds"),
            lineItems: {
                columns: pairCols,
                order: DocumentEntity.Id,
                canReorder: false,
                collection: GrantEntity.GrantClaims,
                render: (args: IGroupRenderFn) => {
                    const { bindingContext, entity } = args.storage.data;
                    const isFormDisabled = args.storage?.isDisabled;
                    const isFromFieldsDisabled = bindingContext.isNew() && (!entity.Name || !entity.NumberOurs);
                    const isClosed = args.storage.data.entity.Balance?.Status?.Code === GrantStatusCode.Closed;
                    const isDisabled = args.props.isDisabled || isFromFieldsDisabled || isFormDisabled || isClosed;


                    const customActionButtons = args.props.isReadOnly ? [] : [{
                        id: "pair",
                        position: ActionButtonPosition.Before,
                        isTransparent: false,
                        title: i18next.t("Document:Pair.ToPair"),
                        isDisabled: isDisabled
                    }];

                    return <SmartFastEntryList
                        isReadOnly={args.props.isReadOnly}
                        isDisabled={isDisabled}
                        useLabelWrapping={true}
                        groupId={PAYMENT_GROUP}
                        isCollapsible={false}
                        storage={args.storage as FormStorage}
                        bindingContext={args.props.bindingContext}
                        customActionButtons={customActionButtons}
                        canAdd={false}
                        isTheOnlyItemRemovable
                        isItemCloneable={false}
                        renderAsFastEntryForReadOnly={!(args.storage as FormStorage)?.isReadOnly}
                        columns={pairCols}
                        order={null}
                        onBlur={args.props.onBlur}
                        onChange={args.props.onChange}
                        onAction={args.props.onAction}
                    />;

                }
            }
        }, {
            id: "linkstable",
            isVisible: not(isNewEntity),
            render: (storage: FormStorage, group: IFormGroupDef) => {
                const groupStatus = storage.getGroupStatus(group.id);

                return (
                    <AllTabsMain>
                        {group.tabs
                            .filter((groupDef) => {
                                return !!groupStatus?.visibleTabs?.[groupDef.id];
                            }).map(tab => {
                                const table = tab.table;
                                const key = storage.data.uuid ? `${table.id}-${storage.data.uuid}` : table.id;

                                return (
                                    <AllTabsWrapper data-testid={TestIds.GrantsDocsTableGroup}>
                                        <AllTabsTableHeader>{tab.title}</AllTabsTableHeader>
                                        {renderFormTable(storage, group, tab.table, storage.data.bindingContext, false, key, group.id)}
                                        {tab.actions?.map(action => {
                                            const link = getDrillDownNavParams({
                                                route: ROUTE_GRANT_JOURNAL,
                                                context: storage.context,
                                                filters: {
                                                    "##GrantJournal_ShortName##": [storage.data.entity.ShortName],
                                                    [BindingContext.localContext("GrantStatuses")]:
                                                        [GrantStatusCode.InProgress, GrantStatusCode.Applied, GrantStatusCode.Closed]
                                                },
                                            });

                                            return <GrantActionButton><Clickable link={link}>{action.title}</Clickable></GrantActionButton>;
                                        })}

                                    </AllTabsWrapper>
                                );
                            })}
                    </AllTabsMain>
                );
            },
            tabs: [{
                id: `links_Grants`,
                title: i18next.t("Grants:Form.UsedDocuments"),
                table: getDocsUsedByGrantDef(),
                actions: [{
                    id: "OpenGrantReport",
                    title: i18next.t("Grants:Form.OpenGrantReport")
                }]
            }, {
                id: `linksDrafts_Grants`,
                title: i18next.t("Grants:Form.UsedDraftDocuments"),
                table: getDraftDocsUsedByGrantDef()
            }]
        }],
        fieldDefinition: {
            ...getNumberRangeFieldDefs(EntitySetName.Grants),
            [GrantEntity.Note]: {
                ...NoteFieldDef
            },
            [GrantEntity.Name]: {
                width: BasicInputSizes.XL
            },
            [GrantEntity.ShortName]: {
                width: BasicInputSizes.M
            },
            [GrantEntity.ContractNumber]: {
                width: BasicInputSizes.L,
            },
            [GrantEntity.DateStart]: {},
            [GrantEntity.DateEnd]: {},
            [GrantEntity.DateDeadlineForSettling]: {},
            ...getBusinessPartnerFieldDef(false),

            [createPath(GrantEntity.GrantClaims, GrantEntryEntity.Document, DocumentEntity.DateAccountingTransaction)]: {
                label: i18next.t("Grants:Form.DateCreated"),
                isReadOnly: true
            },
            [GrantEntity.RequiredParticipation]: {
                fieldSettings: {
                    showSteppers: true,
                    unit: "%"
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        max: 100
                    },
                },
            },
            [createPath(GrantEntity.GrantClaims, GrantClaimEntity.GrantClaimTypeCode)]: {
                label: i18next.t("Grants:Pair.Type"),
                isReadOnly: true,
                formatter: (val: TValue) => {
                    return val ? getEnumDisplayValue(EntityTypeName.GrantClaimType, val as string) : DASH_CHARACTER;
                }
            },
            [createPath(GrantEntity.GrantClaims, GrantClaimEntity.DocumentItem, DocumentItemEntity.Description)]: {
                label: i18next.t("Grants:Pair.Description"),
                width: BasicInputSizes.XXL,
                isReadOnly: true
            },

            [createPath(GrantEntity.GrantClaims, GrantEntryEntity.Document)]: {
                ...getBasePairedDocumentFormFieldDef(),
                label: i18next.t("Grants:Pair.NumberOurs")
            },
            [createPath(GrantEntity.GrantClaims, DocumentItemEntity.Amount)]: {
                type: FieldType.NumberInput,
                label: i18next.t("Grants:Form.Amount"),
                isReadOnly: true,
                formatter: companyCurrencyFormatterWithoutUnit,
                fieldSettings: {
                    unit: getCompanyCurrencyUnit
                },
            },
        }
    };

    const def = {
        table, form,
        translationFiles: getDefinitions.translationFiles,
        entitySet: EntitySetName.Grants
    };

    addBusinessPartnerTableDefs(def, { filterIndex: 0, businessPartnerAsDefault: false, hasLocalBankAcc: false, context });

    return def;
};

getDefinitions.translationFiles = ["Grants", "Document", "Common", "NumberRange", getEnumNameSpaceName(EntityTypeName.GrantClaimType)];
setDefByEntityType(EntityTypeName.Grant, getDefinitions);
